/** GENERATED_ASSET — a Layer collects SVG elements and the exact bounds of what they paint. */
import { Bounds, fmt, Path } from './pathKit';
import type { Pt } from './pathKit';

export interface Style {
  fill?: string; fo?: number;          // fill, fill-opacity
  stroke?: string; sw?: number; so?: number; // stroke, width, stroke-opacity
  op?: number; dash?: string;
  /** false = counts toward paint bounds only (shadows, spray, soft foam). */
  content?: boolean;
  part?: string;
}

export interface GradStop { offset: number; color: string; opacity?: number }

function attrs(s: Style): string {
  const a: string[] = [];
  if (s.part) a.push(`data-part="${s.part}"`);
  a.push(`fill="${s.fill ?? 'none'}"`);
  if (s.fo !== undefined && s.fo !== 1) a.push(`fill-opacity="${fmt(s.fo)}"`);
  if (s.stroke) {
    a.push(`stroke="${s.stroke}"`, `stroke-width="${fmt(s.sw ?? 1)}"`, 'stroke-linejoin="round"', 'stroke-linecap="round"');
    if (s.so !== undefined && s.so !== 1) a.push(`stroke-opacity="${fmt(s.so)}"`);
  }
  if (s.dash) a.push(`stroke-dasharray="${s.dash}"`);
  if (s.op !== undefined && s.op !== 1) a.push(`opacity="${fmt(s.op)}"`);
  return a.join(' ');
}

export class Layer {
  readonly paint = new Bounds();
  readonly content = new Bounds();
  private parts: string[] = [];
  private defs: string[] = [];
  private defIds = new Set<string>();
  constructor(readonly id: string, readonly ns: string) {}

  get isEmpty(): boolean { return this.parts.length === 0; }
  /** Namespaced id, safe when several differently colored instances share one document. */
  ref(local: string): string { return `${this.ns}-${local}`; }

  private reg(b: Bounds, s: Style): void {
    const pad = s.stroke ? (s.sw ?? 1) / 2 : 0;
    this.paint.addBox(b.x0, b.y0, b.x1, b.y1, pad);
    if (s.content !== false) this.content.addBox(b.x0, b.y0, b.x1, b.y1, pad);
  }
  path(p: Path, s: Style): this {
    this.reg(p.bounds, s);
    this.parts.push(`<path ${attrs(s)} d="${p.d()}"/>`); return this;
  }
  poly(pts: readonly Pt[], s: Style, close = true): this { return this.path(new Path().poly(pts, close), s); }
  smooth(pts: readonly Pt[], s: Style, close = true): this { return this.path(new Path().smooth(pts, close), s); }
  line(x0: number, y0: number, x1: number, y1: number, s: Style): this {
    return this.path(new Path().M(x0, y0).L(x1, y1), s);
  }
  ellipse(cx: number, cy: number, rx: number, ry: number, s: Style): this {
    const b = new Bounds().addBox(cx - rx, cy - ry, cx + rx, cy + ry);
    this.reg(b, s);
    this.parts.push(`<ellipse ${attrs(s)} cx="${fmt(cx)}" cy="${fmt(cy)}" rx="${fmt(rx)}" ry="${fmt(ry)}"/>`); return this;
  }
  circle(cx: number, cy: number, r: number, s: Style): this { return this.ellipse(cx, cy, r, r, s); }
  group(open: string, fn: () => void): this {
    this.parts.push(`<g ${open}>`); fn(); this.parts.push('</g>'); return this;
  }

  linear(local: string, x1: number, y1: number, x2: number, y2: number, stops: readonly GradStop[]): string {
    const id = this.ref(local);
    if (!this.defIds.has(id)) {
      this.defIds.add(id);
      const st = stops.map((g) => `<stop offset="${fmt(g.offset)}" stop-color="${g.color}"${g.opacity !== undefined ? ` stop-opacity="${fmt(g.opacity)}"` : ''}/>`).join('');
      this.defs.push(`<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${fmt(x1)}" y1="${fmt(y1)}" x2="${fmt(x2)}" y2="${fmt(y2)}">${st}</linearGradient>`);
    }
    return `url(#${id})`;
  }

  defsMarkup(): string { return this.defs.length ? `<defs>${this.defs.join('')}</defs>` : ''; }
  groupMarkup(): string {
    return `<g data-layer="${this.id}" data-integration="GENERATED_ASSET">${this.parts.join('')}</g>`;
  }
}

export interface SvgDocOpts { width: number; height: number; viewW: number; viewH: number; meta?: string }
export function svgDocument(defs: string, body: string, o: SvgDocOpts): string {
  const meta = o.meta ? `<metadata>${o.meta}</metadata>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${o.width}" height="${o.height}" viewBox="0 0 ${o.viewW} ${o.viewH}">${meta}${defs}${body}</svg>`;
}
